import React, { useState, useEffect } from 'react';
import { useTheme } from '../contexts/ThemeContext';

const PageProgress: React.FC = () => {
  const { colors } = useTheme();
  const [scrollProgress, setScrollProgress] = useState(0);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      const totalHeight = document.documentElement.scrollHeight - window.innerHeight;
      const currentScroll = window.scrollY;
      const progress = (currentScroll / totalHeight) * 100;
      
      setScrollProgress(progress);
      setIsVisible(currentScroll > 200);
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll();

    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <>
      {/* Top Progress Bar */}
      <div 
        className="fixed top-0 left-0 w-full h-1 z-50 transition-opacity duration-300"
        style={{ opacity: isVisible ? 1 : 0 }}
      >
        <div 
          className="h-full transition-all duration-300 ease-out"
          style={{
            width: `${scrollProgress}%`,
            background: `linear-gradient(90deg, ${colors.primary.main} 0%, ${colors.accent.mint} 100%)`
          }}
        />
      </div>

      {/* Floating Progress Circle */}
      <button
        onClick={scrollToTop}
        className={`
          fixed bottom-8 right-8 w-14 h-14 rounded-full shadow-xl
          transition-all duration-500 transform z-40
          ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-20 opacity-0 pointer-events-none'}
        `}
        style={{
          backgroundColor: colors.background.paper,
          boxShadow: `0 10px 30px -10px ${colors.accent.mint}40`
        }}
        aria-label="Scroll to top"
      >
        {/* Progress Ring */}
        <svg className="absolute inset-0 w-full h-full -rotate-90">
          <circle
            cx="28"
            cy="28"
            r="24"
            stroke={`${colors.border}40`}
            strokeWidth="3"
            fill="none"
          />
          <circle
            cx="28"
            cy="28"
            r="24"
            stroke={colors.accent.mint}
            strokeWidth="3"
            fill="none"
            strokeDasharray={`${2 * Math.PI * 24}`}
            strokeDashoffset={`${2 * Math.PI * 24 * (1 - scrollProgress / 100)}`}
            className="transition-all duration-300"
          />
        </svg>
        
        {/* Arrow Icon */}
        <svg 
          className="relative w-6 h-6 mx-auto" 
          fill="none" 
          viewBox="0 0 24 24" 
          stroke={colors.accent.mint}
          strokeWidth={2}
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M5 10l7-7m0 0l7 7m-7-7v18" />
        </svg>
      </button>

      {/* Section Dots Navigator (Optional) */}
      <div 
        className={`
          fixed left-8 top-1/2 transform -translate-y-1/2 space-y-4 z-30
          transition-opacity duration-500 hidden lg:block
          ${isVisible ? 'opacity-100' : 'opacity-0 pointer-events-none'}
        `}
      >
        {[0, 25, 50, 75, 100].map((threshold, index) => (
          <div
            key={index}
            className={`
              w-2 h-2 rounded-full transition-all duration-300 cursor-pointer
              ${scrollProgress >= threshold ? 'w-8' : ''}
            `}
            style={{
              backgroundColor: scrollProgress >= threshold 
                ? colors.accent.mint 
                : `${colors.border}40`
            }}
            onClick={() => {
              const targetScroll = (threshold / 100) * (document.documentElement.scrollHeight - window.innerHeight);
              window.scrollTo({ top: targetScroll, behavior: 'smooth' });
            }}
          />
        ))}
      </div>
    </>
  );
};

export default PageProgress;